<template>
  <div class="_wrapper" :class="{ 'is-shop': displayMode === 'shop' }">
    <div id="product" itemscope itemtype="http://schema.org/Product" class="flex-full" v-if="displayMode === 'food'">
      <section class="px20 product-top-section">
        <div class="container relative">
          <div v-if="isGiftPartner" class="my10">
            <router-link class="back-to-home-category" :to="localizedRoute(`/${currentPartner.location_code}/gifts#category-${product.category[0].category_id}`)">
              <span class="material-icons">chevron_left</span>
              {{ $t('Back to holiday boxes') }}
            </router-link>
          </div>
          <div class="my10">
            <router-link class="back-to-home-category" :to="localizedRoute(`/${currentPartner.location_code}/${currentPartner.code}`)">
              <span class="material-icons">chevron_left</span>
              {{ $t('Back to ') + currentPartner.title }}
            </router-link>
          </div>
          <section class="row m0 around-xs min-h-474">
            <div class="col-xs-12 col-md-6 center-xs middle-xs image">
              <product-gallery
                :offline="image"
                :gallery="gallery"
                :configuration="configuration"
                :product="product"
              />
            </div>
            <div class="col-xs-12 col-md-5 data">
              <h1 class="mb10 mt10 mt0 cl-mine-shaft product-name" data-testid="productName" itemprop="name">
                {{ product.name | htmlDecode }}
                <web-share :title="product.name | htmlDecode" text="Check this product!" class="web-share" />
              </h1>

              <meta itemprop="category" :content="schemaProductCategory" />

              <div itemprop="brand" itemscope itemtype="http://schema.org/Brand" v-if="brandName">
                <meta itemprop="name" :content="brandName">
              </div>

              <meta itemprop="image" :content="galleryImage.src" v-for="galleryImage in gallery">

              <div itemprop="offers" itemscope itemtype="http://schema.org/Offer">
                <meta itemprop="priceCurrency" :content="currentStore.i18n.currencyCode">
                <meta itemprop="price" :content="parseFloat(offerPrice).toFixed(2)">
                <meta itemprop="availability" :content="structuredData.availability">
                <meta itemprop="url" :content="`${getHostPath}${productLink}`">
                <meta itemprop="priceValidUntil" :content="priceValidUntil" v-if="priceValidUntil">
                <div itemprop="shippingDetails" itemscope itemtype="http://schema.org/OfferShippingDetails" v-if="shippingRateAmount !== null">
                  <div itemprop="shippingRate" itemscope itemtype="http://schema.org/MonetaryAmount">
                    <meta itemprop="value" :content="shippingRateAmount">
                    <meta itemprop="currency" :content="currentStore.i18n.currencyCode">
                  </div>
                  <div itemprop="shippingDestination" itemscope itemtype="http://schema.org/DefinedRegion">
                    <meta itemprop="addressCountry" content="UA">
                  </div>
                </div>
                <div
                  class="cl-primary variants"
                  v-if="product.type_id =='configurable' && !loading && !isDisabledProduct"
                >
                  <div class="error" v-if="product.errors && Object.keys(product.errors).length > 0">
                    {{ product.errors | formatProductMessages }}
                  </div>
                  <div
                    class="h5"
                    v-for="(option, index) in product.configurable_options"
                    v-if="(!product.errors || Object.keys(product.errors).length === 0) && Object.keys(configuration).length > 0"
                    :key="index"
                  >
                    <div class="variants-label" data-testid="variantsLabel">
                      {{ option.label }}
                      <span class="weight-700">
                      {{ configuration[option.attribute_code ? option.attribute_code : option.label.toLowerCase()].label }}
                    </span>
                    </div>
                    <div class="row top-xs m0 pt15 pb40 variants-wrapper">
                      <div v-if="option.label == 'Color'">
                        <color-selector
                          v-for="(c, i) in options[option.attribute_code]"
                          v-if="isOptionAvailable(c)"
                          :key="i"
                          :id="c.id"
                          :label="c.label"
                          context="product"
                          :code="option.attribute_code"
                          :class="{ active: c.id == configuration[option.attribute_code].id }"
                        />
                      </div>
                      <div :class="option.attribute_code" v-else>
                        <generic-selector
                          v-for="(s, i) in options[option.attribute_code]"
                          v-if="isOptionAvailable(s)"
                          :key="i"
                          :id="s.id"
                          :label="s.label"
                          context="product"
                          :code="option.attribute_code"
                          class="mr10 mb10"
                          :class="{ active: s.id == configuration[option.attribute_code].id }"
                          v-focus-clean
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <product-custom-options
                v-if="product.custom_options && product.custom_options.length > 0 && product.custom_options.filter(option => option.is_active === 1).length && !loading && !isDisabledProduct"
                :key="product.entity_id"
                :product="product"
                @changeCustomOptionsTotal="changeCustomOptionsTotal"
              />

              <div id="decimal-qty-price">
              <div class="qty-price">
                <div class="center-self m0 p0 flex col-md-6 col-xs-6 col-sm-6 qty-mobile mobile" v-if="!product.stock.is_qty_decimal && product.type_id !== 'grouped' && product.type_id !== 'bundle' && !isDisabledProduct">
                  <div class="col-md-3 col-xs-6 pl0 " :class="{ 'qty-mobile' : product.stock.is_qty_decimal }">
                    <base-input-number
                      v-if="!product.stock.is_qty_decimal"
                      :name="$t('Quantity')"
                      v-model="product.qty"
                      :min="product.stock.min_sale_qty"
                      :qty="product.stock.qty"
                      :sku="product.sku"
                      :qty-increments="product.stock.qty_increments"
                      :qty-in-cart="qtyInCart"
                      :on-disable="onDisable"
                      @blur="$v.$touch()"
                      @refresh="refresh"
                      :validations="[
                          {
                            condition: $v.product.qty.$error && !$v.product.qty.minValue,
                            text: $t('Quantity must be above 0')
                          }
                        ]"
                    />
                    <base-input-decimal-number
                      v-else
                      :name="$t('Quantity')"
                      v-model="product.qty"
                      :min="product.stock.min_sale_qty"
                      :qty="product.stock.qty"
                      :sku="product.sku"
                      :qty-increments="product.stock.qty_increments"
                      :qty-in-cart="qtyInCart"
                      :on-disable="onDisable"
                      @blur="$v.$touch()"
                      @refresh="refresh"
                      :validations="[
                          {
                            condition: $v.product.qty.$error && !$v.product.qty.minValue,
                            text: $t('Quantity must be above 0')
                          }
                        ]"
                    />
                  </div>
                </div>

                <div class="mb10 pb10 cl-secondary sku-product decimal-mobile-sky" :class="{'disabled': isDisabledProduct}">
                  {{ $t('SKU') }}: <span itemprop="sku">{{ product.sku }}</span>
                </div>


                  <div class="center-self m0 p0 flex col-md-6 col-xs-6 col-sm-6 qty-mobile" :class="{ 'desktop' : !product.stock.is_qty_decimal }" v-if="product.type_id !== 'grouped' && product.type_id !== 'bundle' && !isDisabledProduct">
                    <div class="col-md-3 col-xs-6 pl0 " :class="{ 'qty-mobile' : product.stock.is_qty_decimal }">
                      <base-input-number
                        v-if="!product.stock.is_qty_decimal"
                        :name="$t('Quantity')"
                        v-model="product.qty"
                        :min="product.stock.min_sale_qty"
                        :qty="product.stock.qty"
                        :sku="product.sku"
                        :qty-increments="product.stock.qty_increments"
                        :qty-in-cart="qtyInCart"
                        :on-disable="onDisable"
                        @blur="$v.$touch()"
                        @refresh="refresh"
                        :validations="[
                          {
                            condition: $v.product.qty.$error && !$v.product.qty.minValue,
                            text: $t('Quantity must be above 0')
                          }
                        ]"
                      />
                      <base-input-decimal-number
                        v-else
                        :name="$t('Quantity')"
                        v-model="product.qty"
                        :min="product.stock.min_sale_qty"
                        :qty="product.stock.qty"
                        :sku="product.sku"
                        :qty-increments="product.stock.qty_increments"
                        :qty-in-cart="qtyInCart"
                        :on-disable="onDisable"
                        @blur="$v.$touch()"
                        @refresh="refresh"
                        :validations="[
                          {
                            condition: $v.product.qty.$error && !$v.product.qty.minValue,
                            text: $t('Quantity must be above 0')
                          }
                        ]"
                      />
                    </div>
                  </div>
                  <div
                    class="cl-mine-shaft weight-700 retreat col-md-6 col-xs-6"
                    v-if="product.priceInclTax && product.stock.is_qty_decimal && !isDisabledProduct"
                  >
                    {{ product.qty > 0 ? product.priceInclTax * product.qty : product.priceInclTax | price }}
                  </div>
                </div>
              </div>
              <price class="price-section" :class="{'disabled': isDisabledProduct}" v-if="product.type_id !== 'grouped'" :product="product" :qty="product.qty" :options="customOptionsTotal.total" />
              <p class="m0 cl-secondary medium disabled-info" v-if="isDisabledProduct">{{ $t('The product is temporarily unavailable') }}</p>
              <div class="mt-2 weight" v-if="product.weight">
                <span>{{ $t('Weight') }}:</span>
                <span itemprop="weight">{{ product.weight | toWeight }}</span>
              </div>
              <div class="row m0" v-if="!isDisabledProduct">
                <div class="services col-md-12 col-xs-12 center-xs "
                     :class="{ 'flex' : store.shipping.hasOwnProperty('delivery_time') && store.shipping.delivery_time }"
                     v-if="store && ( (store.shipping.hasOwnProperty('delivery_time') && store.shipping.delivery_time) || store.minimal_order_amount )"
                >
                  <div class="left-block-info-store col-md-6 col-xs-6 start-xs"
                       v-if="store.shipping.hasOwnProperty('delivery_time') && store.shipping.delivery_time"
                       :style="{'align-self' : !store.minimal_order_amount ? 'center' : ''}"
                  >
                    <div class="info-store">
                      <div class="left-info-block center-xs" :style="{'padding' : !store.minimal_order_amount ? '3%' : ''}">
                        <span class="material-icons">alarm</span>
                        {{$t('Delivery')}} - {{ store.shipping.delivery_time }}
                      </div>
                    </div>
                  </div>
                  <div class="right-block-info-store col-md-6 col-xs-6 end-xs">
                    <div class="info-store" v-if="store.minimal_order_amount">
                      <div class="right-info-block center-xs inline-flex middle-xs">
                        <img class="wallet-icon" src="/assets/icons/icon-wallet.png" alt="Wallet" width="19" height="18" loading="eager" fetchpriority="high">
                        {{$t('Min. order.')}}. - {{ store.minimal_order_amount | price }}
                      </div>
                    </div>
                  </div>
                </div>
                <add-to-cart
                  :product="product"
                  v-if="isCheckoutAllowed && activeButtonAddToCart && !isDisabledProduct"
                  :disabled="($v.product.qty.$error && !$v.product.qty.minValue) || onDisable"
                  class="col-xs-12 col-sm-12 col-md-12 block-button-add-to-cart"
                />
              </div>
              <div class="my10 start-md center-xs" v-if="onDisable">
                <span class="text-about-qvt-product">
                  {{ $t('The rest is in your basket') }}
                </span>
              </div>
              <section class="container pl0 pt30 pb35 cl-accent details" v-if="product.description">
                <div class="flex pointer" @click="showDetailsWrapper=!showDetailsWrapper">
                  <h2 class="h4 m0 mb10 serif lh20 details-title align-left col-md-12 col-sm-12 col-xs-12">
                    {{ $t('Product details') }}
                  </h2>
                  <span class="material-icons" v-if="showDetailsWrapper">keyboard_arrow_up</span>
                  <span class="material-icons" v-else>keyboard_arrow_down</span>
                </div>
                <div
                  class="h4 details-wrapper"
                  :class="{'details-wrapper--open': detailsOpen}"
                  v-if="showDetailsWrapper"
                >
                  <div class="row between-md m0">
                    <div class="col-xs-12 col-sm-12 col-md-12">
                      <div
                        class="lh30 h5 description"
                        itemprop="description"
                        v-html="product.description"
                      />
                    </div>
                    <div class="col-xs-12 col-sm-5">
                      <ul class="attributes p0 pt5 m0">
                        <product-attribute
                          :key="attr.attribute_code"
                          v-for="attr in customAttributes"
                          :product="product"
                          :attribute="attr"
                          empty-placeholder="N/A"
                        />
                      </ul>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </section>
        </div>
      </section>
      <recommended-products :key="product.entity_id" />
    </div>
    <div class="block mt20" v-else-if="displayMode === 'shop'">
      <product-modal />
    </div>
  </div>
</template>

<script async>
import config from 'config'
import Product from '@vue-storefront/core/pages/Product';
import VueOfflineMixin from 'vue-offline/mixin';
import focusClean from 'theme/components/theme/directives/focusClean';
import WebShare from '@vue-storefront/core/modules/social-share/components/WebShare';
import { minValue } from 'vuelidate/lib/validators';
import { mapActions, mapGetters } from 'vuex';
import { canShowSpecialPriceForProduct } from '@vue-storefront/core/modules/catalog/helpers/tax';
import { getMandatoryCustomOptionsPrice } from 'theme/helpers/customOptions'
import moment from 'moment'

const ProductModal = () => import(/* webpackChunkName: "vsf-product-modal" */ 'theme/components/core/blocks/Shop/Product/ProductModal');
const ProductCustomOptions = () => import(/* webpackChunkName: "vsf-product-modal-custom-options" */ 'theme/components/core/ProductCustomOptions');
const RecommendedProducts = () => import(/* webpackChunkName: "product-recommended-products" */ 'theme/components/core/blocks/Product/Recommendations')
const Price = () => import(/* webpackChunkName: "vsf-price" */ 'theme/components/core/blocks/Product/Price.vue');
const AddToCart = () => import(/* webpackChunkName: "vsf-add-to-cart" */ 'theme/components/core/AddToCart.vue');
const GenericSelector = () => import(/* webpackChunkName: "vsf-generic-selector" */ 'theme/components/core/GenericSelector');
const ColorSelector = () => import(/* webpackChunkName: "vsf-color-selector" */ 'theme/components/core/ColorSelector.vue');
const ProductAttribute = () => import(/* webpackChunkName: "vsf-product-attribute" */ 'theme/components/core/ProductAttribute.vue');
const ProductGallery = () => import(/* webpackChunkName: "vsf-product-gallery" */ 'theme/components/core/ProductGallery');
const BaseInputNumber = () => import(/* webpackChunkName: "vsf-base-input-number" */ 'theme/components/core/blocks/Form/BaseInputNumber');
const BaseInputDecimalNumber = () => import(/* webpackChunkName: "vsf-base-input-decimal-number" */ 'theme/components/core/blocks/Form/BaseInputDecimalNumber');

export default {
  components: {
    AddToCart,
    ColorSelector,
    GenericSelector,
    ProductAttribute,
    ProductCustomOptions,
    ProductGallery,
    RecommendedProducts,
    Price,
    WebShare,
    BaseInputNumber,
    BaseInputDecimalNumber,
    ProductModal
  },
  mixins: [Product, VueOfflineMixin],
  data () {
    return {
      detailsOpen: false,
      currentQty: 0,
      showDetailsWrapper: true,
      customOptionsTotal: {
        options: [],
        singleChoice: {},
        total: 0
      }
    }
  },
  directives: { focusClean },
  validations: {
    product: {
      qty: {
        minValue: minValue(config.restrictions.productMinQty.isDecimal)
      }
    }
  },
  beforeMount () {
    this.changeProductQty();
  },
  computed: {
    ...mapGetters({
      store: 'partners/getCurrentPartner',
      currentLocation: 'locations/getActiveLocation',
      currentPartner: 'partners/getCurrentPartner',
      displayMode: 'partners/getCurrentPartnerDisplayMode'
    }),
    mandatoryOptionsPrice () {
      return getMandatoryCustomOptionsPrice(this.product)
    },
    schemaProductCategory () {
      // K2-3027 follow-up: the API returns product.category as an unordered
      // flatten-and-dedupe of every category branch the product is directly
      // or ancestrally assigned to (see ProductCategoriesTreeResolver on the
      // backend) - it is NOT reliably root->leaf sorted for a single branch,
      // so picking just the last element can surface an unrelated/promo
      // category instead of the product's real department. Emitting the
      // full '>'-joined path (as Google's Product structured data expects
      // anyway) is a safer, purely-frontend improvement: it can't lose the
      // correct category name even when the array ordering is off.
      if (this.product && this.product.category && this.product.category.length > 0) {
        return this.product.category
          .map(category => category && category.name)
          .filter(name => name && name !== 'null') // backend falls back to the literal string 'null' when a category has no name
          .join(' > ')
      }
      return ''
    },
    brandName () {
      // Prefer the product's own trade mark attribute (closer to schema.org's
      // manufacturer-style "brand") where set; fall back to the selling
      // partner/restaurant name, which is the common convention for
      // food-delivery structured data.
      return (this.product && this.product.trade_mark) || (this.currentPartner && this.currentPartner.title) || ''
    },
    priceValidUntil () {
      if (canShowSpecialPriceForProduct(this.product) && this.product.special_to_date) {
        return moment(this.product.special_to_date).format('YYYY-MM-DD')
      }
      return null
    },
    shippingRateAmount () {
      // location.base_delivery_price is the same "starting from" rate already
      // shown publicly on partner cards/category pages (HomePartnerCard,
      // CategorySeoContent) - reusing it keeps structured data consistent
      // with what's on-page instead of inventing a new number. It's a static
      // approximation (actual delivery price is cart-total-tiered), which
      // Google's OfferShippingDetails accepts as a representative rate.
      return (this.currentLocation && this.currentLocation.base_delivery_price)
        ? parseFloat(this.currentLocation.base_delivery_price).toFixed(2)
        : null
    },
    offerPrice () {
      let showSpecialPriceForProduct = canShowSpecialPriceForProduct(this.product)
      if (showSpecialPriceForProduct && !this.product.stock.is_qty_decimal) {
        return (parseFloat(this.product.special_price) + this.mandatoryOptionsPrice + this.customOptionsTotal.total) * this.product.qty
      }

      if (showSpecialPriceForProduct && this.product.stock.is_qty_decimal) {
        return parseFloat(this.product.special_price) + this.mandatoryOptionsPrice + this.customOptionsTotal.total
      }

      if (!showSpecialPriceForProduct && this.product.price && !this.product.stock.is_qty_decimal) {
        return !!this.product.qty
          ? (parseFloat(this.product.price) + this.mandatoryOptionsPrice + this.customOptionsTotal.total) * this.product.qty
          : parseFloat(this.product.price) + this.mandatoryOptionsPrice + this.customOptionsTotal.total
      }

      if (!showSpecialPriceForProduct && this.product.price && this.product.stock.is_qty_decimal) {
        return parseFloat(this.product.price) + this.mandatoryOptionsPrice + this.customOptionsTotal.total
      }

      return 0
    },
    isCheckoutAllowed () {
      return (this.currentLocation.hasOwnProperty('is_checkout_allowed')) ? this.currentLocation.is_checkout_allowed : true
    },
    // K2-3036: used to duplicate only the partner half of isDisabledProduct, omitting the
    // product's own status - the template's own v-if on the add-to-cart button below is now
    // additionally ANDed with !isDisabledProduct too (see ProductModal.vue's already-correct
    // equivalent), but this computed's own name promises "should the button be active", so it
    // should be right on its own too, not rely on every call site remembering to also check
    // isDisabledProduct.
    activeButtonAddToCart () {
      if (!this.currentPartner) {
        return false
      }
      return this.currentPartner.active && this.currentPartner.enabled && !this.isDisabledProduct
    },
    structuredData () {
      return {
        availability: (this.product.stock.is_in_stock && !this.isDisabledProduct) ? 'InStock' : 'OutOfStock'
      }
    },
    cartItem () {
      return this.$store.getters['cart/getCartItems'].find(item => item.entity_id === this.product.entity_id)
    },
    qtyInCart () {
      if (this.cartItem) {
        return parseFloat(this.cartItem.qty).toFixed(2)
      }

      return 0
    },
    onDisable () {
      return (this.product.stock.qty - parseFloat(this.qtyInCart)).toFixed(2) <= 0.1
    },
    isGiftPartner () {
      return this.currentPartner && this.currentPartner.code === 'gift';
    }
  },
  methods: {
    ...mapActions({
      cleanUpCategory: 'category/cleanUpCategory'
    }),
    notifyOutStock () {
      this.$store.dispatch('notification/spawnNotification', {
        type: 'error',
        message: this.$t('The product is out of stock and cannot be added to the cart!'),
        action1: { label: this.$t('OK') }
      })
    },
    notifyWrongAttributes () {
      this.$store.dispatch('notification/spawnNotification', {
        type: 'warning',
        message: this.$t('No such configuration for the product. Please do choose another combination of attributes.'),
        action1: { label: this.$t('OK') }
      })
    },
    changeProductQty () {
      if (this.product.stock.is_qty_decimal === true) {
        this.product.qty = this.product && this.product.stock.min_sale_qty ? this.product.stock.min_sale_qty : config.restrictions.productMinQty.isDecimal;
      }
    },
    refresh (event) {
      let newQty = event.newValue;
      this.product.qty = newQty;
    },
    changeCustomOptionsTotal (option, type, optionId) {
      if (!option) return;

      if (type === 'checkbox') {
        const optionIndex = this.customOptionsTotal.options.findIndex(item => item.option_type_id === option.option_type_id);
        if (optionIndex !== -1) {
          this.customOptionsTotal.options[optionIndex] = option;
        } else {
          this.customOptionsTotal.options.push(option);
        }
      }

      if (type === 'select' || type === 'radio') {
        this.customOptionsTotal.singleChoice = Object.assign({}, this.customOptionsTotal.singleChoice, { [optionId]: option.price });
      }

      const singleChoiceTotal = Object.keys(this.customOptionsTotal.singleChoice).reduce((total, key) => {
        return total + this.customOptionsTotal.singleChoice[key];
      }, 0);

      this.customOptionsTotal.total =
        singleChoiceTotal +
        this.customOptionsTotal.options.reduce((total, item) => {
          return total + item.price * item.qty;
        }, 0)
    }
  },
  beforeDestroy () {
    if (this.$route.name !== 'seo-category') {
      this.cleanUpCategory();
    }
  },
  metaInfo () {
    const meta = Product.metaInfo.call(this)
    if (!this.image) return meta

    return {
      ...meta,
      link: [...(meta.link || []), { rel: 'preload', as: 'image', href: this.image.src }]
    }
  }
}
</script>

<style scoped lang="scss">
.mt-2 {
  margin-top: 8px;
}

.weight {
  font-size: 16px;
  margin-bottom: 8px;
  color: #4F4F4F;
}

.description >>> li {
  list-style: none;
}

.description >>> li::before {
  content: "\2022";
  color: #11bb2e;
  font-weight: bold;
  display: inline-block;
  width: 1em;
  font-size: 22px;
  margin-left: -1em;
}

.disabled-info {
  font-size: 18px;
  border-bottom: 1px solid #f9f8f6;
  padding-bottom: 10px;
}

.price-section {
  box-sizing: border-box;
  min-height: 44px;
}
</style>

<style lang="scss">
  .description {
    li {
      p {
        display: inline;
      }
    }
  }
  .min-h-474 {
    min-height: 474px;
  }
</style>

<style lang="scss" scoped>
@import '~theme/css/variables/colors';
@import '~theme/css/helpers/functions/color';
$color-primary: color(primary);
$color-tertiary: color(tertiary);
$color-secondary: color(secondary);
$color-white: color(white);
$bg-secondary: color(secondary, $colors-background);
._wrapper {
  &.is-shop {
    @media only screen and (max-width: 576px) {
      width: calc(100% - 30px);
      padding: 0 15px;
    }
  }
}

.block {
  width: 850px;

  @media only screen and (max-width: 1200px) {
    width: 654px;
  }

  @media only screen and (max-width: 991px) {
    width: 722px;
  }

  @media only screen and (max-width: 768px) {
    width: 530px;
  }

  @media only screen and (max-width: 576px) {
    width: 100%;
  }
}

.price {
  .h4 {
    @media (max-width: 768px) {
      font-size: 18px !important;
    }
  }
}
.breadcrumbs-desktop{
  @media (max-width: 1200px) {
    display: none;
  }
}

.inline-flex-mobile{
  @media (max-width: 768px) {
    display: inline-flex;
  }
}
.price-before-discount {
  font-size: 16px;
}
.text-about-qvt-product {
  font-size: 14px;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: 0.09px;
  color: #febf00;
}

.block-button-add-to-cart {
  padding: 10px;
}
.block-buttons{
  width: 100%;
}
.sku-product{
  &.disabled {
    margin-bottom: 0;
  }

  &.decimal-mobile-sky{
    @media (max-width: 768px) {
      font-size: 14px;
      text-align: center;
      margin: 0;
      padding: 0;
      align-self: center;
      width: 100%;
    }
  }
  &.mobile {
    @media (max-width: 768px) {
      font-size: 14px;
      width: 100%;
      text-align: center;
      padding: 5px 0 0 0;
    }
  }
  span{
    color: #000000;
  }
}
.product {
  &__add-to-compare {
    display: none;
    @media (min-width: 767px) {
      display: block;
    }
  }
}

.breadcrumbs {
  @media (max-width: 768px) {
    margin: 15px 0;
    padding: 15px 0 0 15px;
  }
}

.error {
  color: red;
  font-weight: bold;
  padding-bottom: 15px;
}
.data {
  @media (max-width: 768px) {
    border-bottom: 1px solid $bg-secondary;
  }
}

.image {
  @media (max-width: 1023px) {
    margin-bottom: 20px;
  }
}

.product-name {
    font-size: 18px;
}

.price {
  border-bottom: 1px solid #f9f8f6;
  @media (max-width: 768px) {
    color: $color-primary;
  }

  &.disabled {
    border-bottom: unset;
    margin-bottom: 0;
  }
}

.variants-label {
  @media (max-width: 768px) {
    font-size: 14px;
  }
}

.variants-wrapper {
  @media (max-width: 768px) {
    padding-bottom: 30px;
  }
}

.product-top-section {
  @media (max-width: 768px) {
    padding: 0;
    background-color: $color-white;
  }
}

.details {
  @media (max-width: 768px) {
    padding: 50px 15px 15px;
  }
}

.details-title {
  padding: 0 8px;

  @media (max-width: 768px) {
    font-size: 18px;
    margin: 0;
  }
}

.details-wrapper {
  @media (max-width: 768px) {
    position: relative;
    overflow: hidden;
    transition: all 0.3s ease;
    font-size: 14px;
  }

  &--open {
    max-height: none;
  }
}

.details-overlay {
  @media (max-width: 768px) {
    position: absolute;
    height: 75%;
    bottom: 0;
    left: 0;
    width: 100%;
    margin: 0;
    cursor: pointer;
    background: linear-gradient(rgba($color-white, 0), rgba($color-white, 1));
    &.hidden {
      display: none;
    }
  }
}

.price-original {
  text-decoration: line-through;
}

.action {
  &:hover {
    color: $color-tertiary;
  }
}

.attributes {
  list-style-type: none;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter,
.fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}

.product-image {
  mix-blend-mode: multiply;
  width: 460px;
}

.web-share {
  float: right;
}

.decimal-qty-title {
  font-size: 20px;
}

#decimal-qty-price {
  width: 100%;
  min-height: 68px;

  .qty-price {
    display: block;
    @media (max-width: 768px) {
      display: inline-flex;
      padding: 10px 0;
      border-top: 1px solid #f9f8f6;
      border-bottom: 1px solid #f9f8f6;
      width: 100%;
    }
  }
  .sku-product {
    border-bottom: 1px solid #f9f8f6;
    @media (max-width: 768px) {
      border: none;
    }
  }
  .desktop {
    display: block;
    @media (max-width: 768px) {
      display: none;
    }
  }
  .mobile {
    display: none;
    @media (max-width: 768px) {
      display: block;
    }
  }
}
.qty-mobile {
  @media (max-width: 768px) {
   justify-content: center;
    text-align: center;
    text-align: -webkit-center;
    text-align: -moz-center;
  }
}
.retreat {
  text-align: center;
  font-size: 18px;
  border-left: 1px solid #f9f8f6;
  padding-left: 10px;
  margin-top: 3px;
  margin-left: 8px;
  @media(max-width: 768px) {
    margin-top: 0;
    text-align: center;
    padding-top: 3px;
    margin-left: 0;
  }
}

.store-name {
  margin: 8px 0;
}
.services {
  padding: 5px 0 8px 0;
}
.services {
  .left-block-info-store {
    padding-left: 0;
    padding-right: 6px;
    @media (max-width: 990px) and (min-width: 765px){
      padding-right: 0;
    }
    @media (max-width: 365px) {
      padding-right: 0;
    }
  }
  .right-block-info-store {
    padding-right: 0;
    padding-left: 6px;
  }
}
.info-store {
  .left-info-block {
    display: flex;
    border-radius: 10px;
    background-color: #f9f8f6;
    height: 100%;
    align-items: center;
    color: #11bb2e;
    font-size: 12px;

    .material-icons {
      margin: 0;
      padding-right: 4px;
      color: #11bb2e;
      font-size: 15px;
      @media (max-width: 990px) and (min-width: 765px) {
        padding-right: 2px;
      }
      @media(max-width: 425px) {
        padding-right: 2px;
      }
    }
  }

  .right-info-block {
    display: flex;
    border-radius: 10px;
    background-color: #f9f8f6;
    padding: 3%;
    color: #11bb2e;
    font-size: 12px;
    .wallet-icon {
      margin-right: 2px;
    }
  }
}
.back-to-home-category {
  margin-top: 20px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  font-weight: 700;
  color: #000000;
  &:hover {
    color: #7e7171;
  }
}

.back-to-home-category.underline:after, .back-to-home-category:not(.no-underline):hover:after {
  background: none;
}
</style>
